import { parseJpegDimensions } from '@/lib/reporting/jpeg';
import { journalExportToPdf, type JournalPdfOptions } from './exportJournalPdf';
import {
  assignJournalBilag,
  journalExportInnerPdfFilename,
  journalExportZipFilename,
  type JournalBilagItem,
  type JournalExportDocument,
} from './journalExportData';
import {
  DEFAULT_JOURNAL_EXPORT_MODE,
  JOURNAL_EXPORT_MODES,
  isEmbeddableJournalAttachment,
  type JournalExportMode,
} from './journalExportMode';
import { buildStoreZip } from './zipStore';

export type JournalExportErrorCode = 'attachment_missing' | 'pdf_failed' | 'zip_failed';

export class JournalExportError extends Error {
  readonly code: JournalExportErrorCode;

  constructor(code: JournalExportErrorCode, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'JournalExportError';
    this.code = code;
  }
}

export type JournalAttachmentFetcher = (documentId: string) => Promise<Uint8Array>;

export interface JournalExportPackage {
  zipBytes: Uint8Array | null;
  zipFilename: string | null;
  pdfBytes: Uint8Array;
  pdfFilename: string;
  downloadBytes: Uint8Array;
  downloadFilename: string;
  downloadMimeType: 'application/pdf' | 'application/zip';
  mode: JournalExportMode;
}

async function fetchAttachmentBytes(
  fetcher: JournalAttachmentFetcher,
  item: JournalBilagItem,
): Promise<Uint8Array> {
  if (!item.documentId) {
    throw new JournalExportError('attachment_missing', 'Journal attachment could not be retrieved');
  }
  let bytes: Uint8Array;
  try {
    bytes = await fetcher(item.documentId);
  } catch (error) {
    if (error instanceof JournalExportError) throw error;
    throw new JournalExportError(
      'attachment_missing',
      'Journal attachment could not be retrieved',
      {
        cause: error,
      },
    );
  }
  if (bytes.length === 0) {
    throw new JournalExportError('attachment_missing', 'Journal attachment could not be retrieved');
  }
  return bytes;
}

function buildPdf(
  document: JournalExportDocument,
  pdfOptions: JournalPdfOptions | undefined,
  generatedAt: Date,
): Uint8Array {
  try {
    return journalExportToPdf(document, {
      ...pdfOptions,
      generatedAt,
    });
  } catch (error) {
    throw new JournalExportError('pdf_failed', 'Journal PDF could not be generated', {
      cause: error,
    });
  }
}

function wrapZip(
  pdfFilename: string,
  pdfBytes: Uint8Array,
  files: { path: string; data: Uint8Array }[],
  generatedAt: Date,
): Uint8Array {
  try {
    return buildStoreZip([{ path: pdfFilename, data: pdfBytes }, ...files], generatedAt);
  } catch (error) {
    throw new JournalExportError('zip_failed', 'Journal export package could not be created', {
      cause: error,
    });
  }
}

export async function buildJournalExportPackage(input: {
  document: JournalExportDocument;
  fetchAttachment: JournalAttachmentFetcher;
  mode?: JournalExportMode;
  pdfOptions?: JournalPdfOptions;
  generatedAt?: Date;
}): Promise<JournalExportPackage> {
  const mode = input.mode ?? DEFAULT_JOURNAL_EXPORT_MODE;
  const generatedAt = input.generatedAt ?? new Date(input.document.exportedAt);
  const pdfFilename = journalExportInnerPdfFilename(input.document.patientName);
  const zipFilename = journalExportZipFilename(
    input.document.patientName,
    Number.isNaN(generatedAt.getTime()) ? new Date() : generatedAt,
  );

  switch (mode) {
    case JOURNAL_EXPORT_MODES.journal_only: {
      const pdfBytes = buildPdf(
        input.document,
        { ...input.pdfOptions, includeBilag: false },
        generatedAt,
      );
      return {
        zipBytes: null,
        zipFilename: null,
        pdfBytes,
        pdfFilename,
        downloadBytes: pdfBytes,
        downloadFilename: pdfFilename,
        downloadMimeType: 'application/pdf',
        mode,
      };
    }
    case JOURNAL_EXPORT_MODES.journal_with_bilag: {
      const { document, bilag } = assignJournalBilag(input.document);
      const files: { path: string; data: Uint8Array }[] = [];
      for (const item of bilag) {
        files.push({
          path: item.zipPath,
          data: await fetchAttachmentBytes(input.fetchAttachment, item),
        });
      }
      const pdfBytes = buildPdf(document, input.pdfOptions, generatedAt);
      const zipBytes = wrapZip(pdfFilename, pdfBytes, files, generatedAt);
      return {
        zipBytes,
        zipFilename,
        pdfBytes,
        pdfFilename,
        downloadBytes: zipBytes,
        downloadFilename: zipFilename,
        downloadMimeType: 'application/zip',
        mode,
      };
    }
    case JOURNAL_EXPORT_MODES.journal_with_embedded_bilag: {
      const { document, bilag } = assignJournalBilag(input.document);
      const leftover: { path: string; data: Uint8Array }[] = [];
      const embeddedByDocumentId: Record<string, Uint8Array> = {};
      for (const item of bilag) {
        const bytes = await fetchAttachmentBytes(input.fetchAttachment, item);
        if (
          item.documentId &&
          isEmbeddableJournalAttachment(item.mimeType) &&
          parseJpegDimensions(bytes)
        ) {
          embeddedByDocumentId[item.documentId] = bytes;
        } else {
          leftover.push({ path: item.zipPath, data: bytes });
        }
      }
      const pdfBytes = buildPdf(
        document,
        { ...input.pdfOptions, includeBilag: true, embeddedByDocumentId },
        generatedAt,
      );
      if (leftover.length === 0) {
        return {
          zipBytes: null,
          zipFilename: null,
          pdfBytes,
          pdfFilename,
          downloadBytes: pdfBytes,
          downloadFilename: pdfFilename,
          downloadMimeType: 'application/pdf',
          mode,
        };
      }
      const zipBytes = wrapZip(pdfFilename, pdfBytes, leftover, generatedAt);
      return {
        zipBytes,
        zipFilename,
        pdfBytes,
        pdfFilename,
        downloadBytes: zipBytes,
        downloadFilename: zipFilename,
        downloadMimeType: 'application/zip',
        mode,
      };
    }
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}
